import React, { useCallback, useContext } from "react";
import { pluralize } from "@common/helpers/string";
import { numberFormatter } from "@common/helpers/number";
import { Pagination } from "@design-system/Pagination/Pagination";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { usePaginationWithBulkReset } from "@components/transactions/hooks/usePaginationWithBulkReset";
import { TransactionsTotals } from "@components/transactions/TransactionsTotals";
import {
    PaginatedTransactionsActionsContext,
    PaginatedTransactionsDataContext,
} from "@components/transactions/PaginatedTransactionsTable.context";
import { TransactionBulkModalsFromActions } from "@components/transactions/TransactionsBulkActions/TransactionBulkModalsFromActions";
import { useFooterPassingStatistics } from "@components/transactions/views/useFooterPassingStatistics";
import { HStack } from "@design-system/HStack/HStack";
import { useAccrualReviewBulkActions } from "./useAccrualReviewBulkActions";

interface AccrualReviewBulkActionsFooterProps {
    scrollTo?: (options: ScrollToOptions) => void;
}

export const AccrualReviewBulkActionsFooter = ({
    scrollTo,
}: AccrualReviewBulkActionsFooterProps) => {
    const { currentPage, totalPages, transactions } = useContext(
        PaginatedTransactionsDataContext,
    );
    const { setPage } = useContext(PaginatedTransactionsActionsContext);

    const bulkActions = useAccrualReviewBulkActions();
    const passingStatistics = useFooterPassingStatistics();

    const onPageChange = useCallback(
        (newPage: number) => {
            setPage(newPage);
            scrollTo?.({ top: 0, behavior: "smooth" });
        },
        [setPage, scrollTo],
    );

    const pageChangeWithBulkActionReset = usePaginationWithBulkReset({
        onPageChange,
    });

    if (!transactions || transactions.length === 0) {
        return null;
    }

    const hasSelection = bulkActions.selectedCount > 0;
    const hasPagination = totalPages > 1;
    const isBusy = bulkActions.isBusy;
    const acceptableCount =
        bulkActions.pendingClassification?.acceptableCount ?? 0;

    return (
        <>
            <CommandIsland
                sticky
                className={commandIslandStyles.bulkActionsStickyFooter}
            >
                <HStack gap={8}>
                    {hasPagination && (
                        <Pagination
                            pageCount={totalPages}
                            currentPage={currentPage}
                            disabled={isBusy}
                            onPageChange={pageChangeWithBulkActionReset}
                            size="md"
                            buttonsPosition="left"
                        />
                    )}

                    {passingStatistics && (
                        <TransactionsTotals {...passingStatistics} />
                    )}

                    {hasSelection && (
                        <>
                            <Tooltip
                                key={bulkActions.selectedCount}
                                content={`Deselect ${bulkActions.selectedCount}`}
                            >
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={bulkActions.clearSelection}
                                >
                                    {bulkActions.selectedCount} selected
                                </Button>
                            </Tooltip>
                            {bulkActions.showSelectAll && (
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={bulkActions.selectAll}
                                    data-testid="bulk-select-all-button"
                                >
                                    Select all{" "}
                                    {numberFormatter.format(
                                        bulkActions.totalTransactions,
                                    )}
                                </Button>
                            )}
                        </>
                    )}
                </HStack>

                {hasSelection && (
                    <HStack gap={8}>
                        <Button
                            variant="primary"
                            size="md"
                            onClick={
                                bulkActions.hasSelectedAll
                                    ? bulkActions.handleConfirmAll
                                    : bulkActions.handleBulkAccept
                            }
                            disabled={
                                bulkActions.hasSelectedAll
                                    ? bulkActions.isConfirmAllDisabled
                                    : bulkActions.isBulkAcceptDisabled
                            }
                        >
                            Accept accrual ledger
                        </Button>

                        <WithHotkey
                            hotKey="v"
                            hotKeyOptions={{
                                description: "Set counterparty",
                                preventDefault: true,
                            }}
                        >
                            <Button
                                variant="primary"
                                size="md"
                                onClick={bulkActions.handleSetCounterpartyClick}
                                disabled={isBusy}
                            >
                                Set counterparty
                            </Button>
                        </WithHotkey>

                        <WithHotkey
                            hotKey="e"
                            hotKeyOptions={{
                                description: "Set entity",
                                preventDefault: true,
                            }}
                        >
                            <Button
                                variant="primary"
                                size="md"
                                onClick={bulkActions.handleSetEntityClick}
                                disabled={isBusy}
                            >
                                Set entity
                            </Button>
                        </WithHotkey>

                        <Tooltip content="Open bulk actions">
                            <Button
                                variant="primary"
                                size="md"
                                onClick={bulkActions.handleOpenBulkActions}
                                disabled={isBusy}
                                rightIcon={
                                    <span data-slot-box="">
                                        <Shortcut keys="mod+k" />
                                    </span>
                                }
                            >
                                Actions
                            </Button>
                        </Tooltip>
                    </HStack>
                )}
            </CommandIsland>

            <ConfirmationModal
                show={bulkActions.isConfirmModalVisible}
                title={`Accept ${acceptableCount} ${pluralize("transaction", acceptableCount)}?`}
                question={bulkActions.confirmModalMessage}
                yes="Confirm"
                no="Cancel"
                onConfirm={bulkActions.handleConfirm}
                onReject={bulkActions.handleCancelConfirm}
                onHide={bulkActions.handleCancelConfirm}
            />

            <TransactionBulkModalsFromActions bulkActions={bulkActions} />
        </>
    );
};
